'use client';

import { createContext, useState, useCallback, useMemo } from 'react';
import type { User } from '@/modules/tenant';

export type UserContextValue = {
  user: User;
  /** ID of support user impersonating (from JWT act.sub) */
  impersonator?: User['id'];
  /** Update user data on the client without a full page refresh */
  updateUser: (patch: Partial<User>) => void;
};

const UserContext = createContext<UserContextValue | null>(null);

export function UserContextProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: Omit<UserContextValue, 'updateUser'> | null;
}) {
  const [userOverride, setUserOverride] = useState<Partial<User> | null>(null);

  const updateUser = useCallback(
    (patch: Partial<User>) =>
      setUserOverride((prev) => ({ ...prev, ...patch })),
    [],
  );

  const contextValue = useMemo(() => {
    if (!value) return null;

    return {
      ...value,
      user: userOverride ? { ...value.user, ...userOverride } : value.user,
      updateUser,
    };
  }, [value, userOverride, updateUser]);

  return <UserContext value={contextValue}>{children}</UserContext>;
}

export default UserContext;
